import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import GoogleSignInButton from "../components/GoogleSignInButton";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { setToken, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Login — dual-mode entry point.
 *
 * Google OAuth (unchanged) sits at the top since it's the fastest path.
 * Below it, the email + password form covers users who prefer credentials
 * or don't have a Google account. Both flows converge on the same
 * `/auth/me` refresh so downstream context updates are identical.
 *
 * A "Sign up" and "Forgot password?" link lead to standalone pages —
 * keeps the login form focused on its ONE job (log in) and avoids the
 * error-prone tabs-inside-forms UX.
 */
export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      // Same shape as /auth/google — token in body for Bearer fallback +
      // HttpOnly cookie set server-side for the primary session.
      if (data?.token) setToken(data.token);
      setUser(data);
      toast.success(`Welcome back, ${data.name?.split(" ")[0] || "there"}!`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Login failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-zinc-50 dark:bg-zinc-950" data-testid="login-root">
      <div className="pointer-events-none absolute inset-x-0 -top-40 -z-0 h-[420px] bg-[radial-gradient(circle_at_50%_0%,rgba(16,185,129,0.18),transparent_70%)]" aria-hidden="true"/>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col items-center px-6 py-12 sm:py-16">
        <Link to="/" className="flex items-center gap-2" data-testid="login-logo">
          <Logo className="h-9 w-9" />
          <span className="font-heading text-lg font-bold">Syllox</span>
        </Link>

        <div className="mt-8 w-full">
          <div className="mb-1 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"/>
            Built for Aspirants
          </div>
          <h1 className="font-heading text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl" data-testid="login-heading">
            Welcome back.
          </h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400" data-testid="login-subheading">
            Log in to keep the streak alive.
          </p>

          {/* Google — the fast path. Left on top so returning Google users
              don't have to hunt for the button. */}
          <div className="mt-6">
            <GoogleSignInButton testId="login-google-button" />
          </div>

          {/* Divider — makes the transition to the credentials form obvious. */}
          <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-wider text-zinc-400">
            <span className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800"/>
            or with email
            <span className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800"/>
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="login-email-form">
            <div className="space-y-1.5">
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="login-password">Password</Label>
                <Link to="/forgot-password" className="text-xs font-medium text-emerald-600 hover:underline" data-testid="login-forgot-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="login-password"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="login-password-toggle"
                >
                  {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-emerald-500 text-white hover:bg-emerald-600"
              data-testid="login-submit-btn"
            >
              {submitting ? <Loader2 size={16} className="animate-spin"/> : "Log in"}
            </Button>
          </form>

          <p className="mt-5 text-center text-sm text-zinc-600 dark:text-zinc-400">
            New to Syllox?{" "}
            <Link to="/signup" className="font-medium text-emerald-600 hover:underline" data-testid="login-signup-link">
              Create an account
            </Link>
          </p>

          <p className="mt-4 text-center text-xs text-zinc-500 dark:text-zinc-400">
            By continuing, you agree to our{" "}
            <Link to="/terms" className="font-medium text-zinc-700 hover:text-emerald-600 dark:text-zinc-300">Terms</Link>
            {" "}and{" "}
            <Link to="/privacy" className="font-medium text-zinc-700 hover:text-emerald-600 dark:text-zinc-300">Privacy Policy</Link>.
          </p>
        </div>

        <div className="mt-auto pt-12 text-center text-xs text-zinc-400 dark:text-zinc-500">
          © {new Date().getFullYear()} Syllox · Made in India
        </div>
      </div>
    </div>
  );
}
